import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {iso8601DateTimeZodValidator, uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {
    type OttopilotEventContainer,
    ottopilotEventContainerValidator,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainer";
import {z as zod} from "zod";

export type OttopilotCustomer = {
    additionalColumnValues: Record<Uuid, unknown>;
    createdAt: Iso8601DateTime;
    createdBy: Uuid;
    eventContainer: OttopilotEventContainer | null;
    id: Uuid;
    updatedAt: Iso8601DateTime;
    updatedBy: Uuid;
    workspaceId: Uuid;
};

export const ottopilotCustomerValidator: zod.ZodType<OttopilotCustomer> = zod.strictObject({
    additionalColumnValues: zod.record(uuidZodValidator, zod.unknown()),
    createdAt: iso8601DateTimeZodValidator,
    createdBy: uuidZodValidator,
    eventContainer: ottopilotEventContainerValidator.nullable(),
    id: uuidZodValidator,
    updatedAt: iso8601DateTimeZodValidator,
    updatedBy: uuidZodValidator,
    workspaceId: uuidZodValidator,
});

export const ottopilotCustomerEventContainerEmbedSchema = ottopilotEventContainerValidator;

export const ottopilotCustomerApiRowSchema = zod
    .object({
        additionalColumnValues: zod.record(uuidZodValidator, zod.unknown()).optional(),
        createdAt: iso8601DateTimeZodValidator.optional(),
        createdBy: uuidZodValidator.optional(),
        eventContainer: ottopilotCustomerEventContainerEmbedSchema.nullable().optional(),
        id: uuidZodValidator,
        updatedAt: iso8601DateTimeZodValidator.optional(),
        updatedBy: uuidZodValidator.optional(),
        workspaceId: uuidZodValidator,
    });

export function mapOttopilotCustomerApiRowToOttopilotCustomer(row: zod.infer<typeof ottopilotCustomerApiRowSchema>): OttopilotCustomer {
    const createdAt = row.createdAt ?? ("1970-01-01T00:00:00.000Z" as Iso8601DateTime);
    const updatedAt = row.updatedAt ?? createdAt;
    const createdBy = row.createdBy ?? row.id;
    const updatedBy = row.updatedBy ?? createdBy;

    return {
        additionalColumnValues: row.additionalColumnValues ?? {},
        createdAt: createdAt,
        createdBy: createdBy,
        eventContainer: row.eventContainer ?? null,
        id: row.id,
        updatedAt: updatedAt,
        updatedBy: updatedBy,
        workspaceId: row.workspaceId,
    };
}
